import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";

import {
  BackWallTypeDetail,
  BackWallTypeForm,
  BackWallTypeListMeta,
  BackWallTypeRow,
  BackWallTypeUpdatePayload,
} from "../types";
import { initialBackWallTypeForm, initialState } from "./initialState";

export const sliceKey = "backWallTypeState";

type BackWallTypeListSuccessPayload = {
  data: BackWallTypeRow[];
  meta: BackWallTypeListMeta;
};

const backWallTypeSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getBackWallTypeList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getBackWallTypeListSuccess(
      state,
      action: PayloadAction<BackWallTypeListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getBackWallTypeListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<BackWallTypeListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getBackWallTypeDetail(state, _action: PayloadAction<string>) {
      state.detailLoading = true;
      state.detailError = null;
    },

    getBackWallTypeDetailSuccess(
      state,
      action: PayloadAction<BackWallTypeDetail>
    ) {
      state.detailLoading = false;
      state.detail = action.payload;
      state.detailError = null;
    },

    getBackWallTypeDetailError(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.detail = null;
      state.detailError = action.payload;
    },

    clearBackWallTypeDetail(state) {
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialBackWallTypeForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof BackWallTypeForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    addBackWallType(state) {
      state.saving = true;
      state.error = null;
    },

    addBackWallTypeSuccess(state, action: PayloadAction<BackWallTypeRow>) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialBackWallTypeForm;
      state.error = null;
      state.list = placeCreatedRowFirst(state.list, action.payload);
    },

    addBackWallTypeError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateBackWallType(
      state,
      _action: PayloadAction<BackWallTypeUpdatePayload>
    ) {
      state.saving = true;
      state.error = null;
    },

    updateBackWallTypeSuccess(state, action: PayloadAction<BackWallTypeDetail>) {
      state.saving = false;
      state.error = null;
      state.detail = action.payload;
      state.list = state.list.map((item) =>
        item.backWallCode === action.payload.backWallCode
          ? {
              ...item,
              backWallDesc: action.payload.backWallDesc,
              bump: action.payload.bump,
            }
          : item
      );
    },

    updateBackWallTypeError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteBackWallType(state, _action: PayloadAction<string>) {
      state.saving = true;
      state.error = null;
    },

    deleteBackWallTypeSuccess(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter(
        (item) => item.backWallCode !== action.payload
      );
      if (state.detail?.backWallCode === action.payload) {
        state.detail = null;
      }
    },

    deleteBackWallTypeError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: backWallTypeActions, reducer } = backWallTypeSlice;
